import { useEffect, useState } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import language from '../../variables/language';
import useDayParts from '../../hooks/useDayParts';
import DateInput from '../../components/forms/DateInput';
import TimeInput from '../../components/forms/TimeInput';
import { rescheduleActionLabel, rescheduleStatusLabel } from '../../variables/rescheduleActionLabels';
import * as rescheduleApi from './rescheduleApi';
import wsManager from '../../services/wsManager';
import IconCloseX from '../../components/icons/IconCloseX';
import '../../css/modals.css';
import '../../css/exercise-templates.css';
import '../../css/lesson-detail-modern.css';
import '../../css/reschedule.css';

// Shared response modal for BOTH negotiation types a student can raise from
// the platform - `item` is {type: 'reschedule', id} or {type: 'cancel', id,
// raw} (cancel has no GET /{id} detail endpoint, list already carries
// everything needed, so `raw` is the row from the pending-list fetch).
// Reschedule is the rich flow (day-part breakdown, counter-offer, direct
// lesson-time edit, history); cancel is deliberately simple (approve/decline
// only, no voting - per explicit scope call, a cancel never needs group
// consensus). Reschedule subscribes to /topic/reschedule/{lessonId}
// (backend already broadcasts on every state change) and just refetches on
// any message - simpler and more robust than reconciling the pushed payload
// piecemeal into local state. Cancel has no WS topic (backend never
// broadcasts for it - a cancel is a single teacher decision, nothing async
// happens on the student's side afterward), so it stays refetch-on-action.
export default function RescheduleResponseModal({ isOpen, item, onClose, onResolved }) {
   const lang = language[language.lang];
   const dayParts = useDayParts();

   const [detail, setDetail] = useState(null);
   const [status, setStatus] = useState('loading');
   const [error, setError] = useState('');
   const [saving, setSaving] = useState(false);

   const [selectedDayPart, setSelectedDayPart] = useState('');
   const [counterMessage, setCounterMessage] = useState('');
   const [declineMessage, setDeclineMessage] = useState('');
   const [teacherMessage, setTeacherMessage] = useState('');
   const [editingTime, setEditingTime] = useState(false);
   const [timeForm, setTimeForm] = useState({ date: '', start: '', end: '' });

   const load = () => {
      if (!item) return;
      setStatus('loading');
      setError('');
      if (item.type === 'reschedule') {
         // PlatformRescheduleRequestView (what the detail endpoint's `request`
         // field is) is deliberately flat - only `lessonId`, no nested Lesson
         // object - fetch the real lesson separately for date/start/end.
         rescheduleApi.getRescheduleDetail(item.id)
            .then((res) => axios.get('/api/lessons/' + res.data.request.lessonId, { withCredentials: true })
               .then((lessonRes) => setDetail({ ...res.data, lesson: lessonRes.data }))
               .catch(() => setDetail(res.data)))
            .then(() => setStatus('done'))
            .catch(() => setStatus('error'));
      } else {
         // CancelRequestController has no GET /{id} - item.raw is a one-time
         // snapshot from whatever list fetch opened this modal, so after
         // approve/decline it goes stale (status/actions never update in an
         // already-open modal). Refetch via the lesson-scoped list (any
         // status, per its own contract) and find this one by id - same
         // "always re-fetch, never trust the pushed/captured snapshot"
         // philosophy as the reschedule branch above.
         const lessonId = item.raw && item.raw.event && item.raw.event.lesson && item.raw.event.lesson.id;
         if (!lessonId) { setDetail({ cancel: item.raw }); setStatus('done'); return; }
         rescheduleApi.getLessonCancelRequests(lessonId)
            .then((res) => {
               const fresh = (Array.isArray(res.data) ? res.data : []).find((c) => c.id === item.id);
               setDetail({ cancel: fresh || item.raw });
               setStatus('done');
            })
            .catch(() => { setDetail({ cancel: item.raw }); setStatus('done'); });
      }
   };

   useEffect(() => {
      if (isOpen) {
         setSelectedDayPart('');
         setCounterMessage('');
         setDeclineMessage('');
         setTeacherMessage('');
         setEditingTime(false);
         load();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isOpen, item]);

   // Live updates while the modal is open - reschedule only (see file-level
   // comment). lessonId only becomes known once the first load() resolves,
   // so this subscribes a beat after the initial fetch rather than
   // guessing it from `item` (which only carries the request id).
   useEffect(() => {
      if (!isOpen || !item || item.type !== 'reschedule' || !detail || !detail.request) return;
      const lessonId = detail.request.lessonId;
      if (!lessonId) return;
      const topic = '/topic/reschedule/' + lessonId;
      const handler = () => load();
      wsManager.subscribe(topic, handler);
      return () => wsManager.unsubscribe(topic, handler);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isOpen, item, detail && detail.request && detail.request.lessonId]);

   if (!isOpen || !item) return '';

   const stopProp = (e) => e.stopPropagation();

   const handleCounterOffer = () => {
      if (!selectedDayPart || saving) return;
      setSaving(true);
      rescheduleApi.counterOfferReschedule(item.id, selectedDayPart, counterMessage || null)
         .then(load)
         .catch(() => setError('Не вдалося надіслати пропозицію'))
         .finally(() => setSaving(false));
   };

   const handleDecline = () => {
      if (saving) return;
      setSaving(true);
      const action = item.type === 'reschedule' ? rescheduleApi.declineReschedule(item.id, declineMessage || null) : rescheduleApi.declineCancel(item.id, teacherMessage || null);
      action
         .then(() => { load(); if (onResolved) onResolved(); })
         .catch(() => setError('Не вдалося зберегти'))
         .finally(() => setSaving(false));
   };

   const handleApproveCancel = () => {
      if (saving) return;
      setSaving(true);
      rescheduleApi.approveCancel(item.id, teacherMessage || null)
         .then(() => { load(); if (onResolved) onResolved(); })
         .catch(() => setError('Не вдалося зберегти'))
         .finally(() => setSaving(false));
   };

   const openTimeEditor = (lesson) => {
      setTimeForm({ date: lesson.date || '', start: lesson.start || '', end: lesson.end || '' });
      setEditingTime(true);
   };

   const handleProposeTime = (lessonId) => {
      if (saving || !timeForm.date || !timeForm.start || !timeForm.end) return;
      setSaving(true);
      axios.put('/api/lessons/' + lessonId, JSON.stringify(timeForm), { withCredentials: true, headers: { 'Content-Type': 'application/json' } })
         .then(() => { setEditingTime(false); load(); if (onResolved) onResolved(); })
         .catch(() => setError('Не вдалося змінити час заняття'))
         .finally(() => setSaving(false));
   };

   const formatDateWeekday = (date) => {
      if (!date) return '';
      const dt = DateTime.fromFormat(date, 'yyyy-MM-dd');
      if (!dt.isValid) return date;
      return lang.weekDaysFull[dt.weekday - 1] + ', ' + dt.toFormat('dd.MM.yyyy');
   };

   const renderReschedule = () => {
      const req = detail.request;
      const lesson = detail.lesson || {};
      const isActionable = req.status === 'PENDING_TEACHER';

      return (
         <>
            <div className='reschedule-modal-lesson-info'>
               <span className='reschedule-modal-lesson-date'>{formatDateWeekday(lesson.date)}, {lesson.start}–{lesson.end}</span>
               <span className={'reschedule-modal-status-pill status-' + req.status}>{rescheduleStatusLabel(req.status)}</span>
            </div>

            <p className='exercise-block-field-label'>Запропонований день</p>
            <p className='reschedule-modal-proposed-date'>{formatDateWeekday(req.proposedDate)}</p>

            <p className='exercise-block-field-label'>Бажана частина дня</p>
            <div className='reschedule-daypart-grid'>
               {dayParts.map((dp) => (
                  <div key={dp.id}
                     className={'reschedule-daypart-box'
                        + (req.proposedDayPart === dp.id ? ' proposed' : '')
                        + (req.counterOfferDayPart === dp.id ? ' counter-offer' : '')}>
                     <span className='reschedule-daypart-label'>{dp.label}</span>
                     <span className='reschedule-daypart-range'>{dp.startTime}–{dp.endTime}</span>
                     {req.proposedDayPart === dp.id ? <span className='reschedule-daypart-tag'>обрав учень</span> : ''}
                     {req.counterOfferDayPart === dp.id ? <span className='reschedule-daypart-tag counter'>ваша пропозиція</span> : ''}
                  </div>
               ))}
            </div>

            <p className='exercise-block-field-label'>Повідомлення від учня</p>
            <p className='reschedule-modal-message'>{req.message || '—'}</p>

            {detail.participants && detail.participants.length > 1 ? (
               <>
                  <p className='exercise-block-field-label'>Учасники заняття</p>
                  <div className='reschedule-participants-list'>
                     {detail.participants.map((p) => (
                        <span key={p.id} className={'reschedule-participant-chip vote-' + p.vote}>
                           {p.student ? (p.student.name + ' ' + p.student.surname) : '—'} - {p.vote === 'ACCEPTED' ? 'погодився(лась)' : p.vote === 'PROPOSED_ALTERNATIVE' ? 'запропонував(ла) інше' : 'очікуємо'}
                        </span>
                     ))}
                  </div>
               </>
            ) : ''}

            {isActionable ? (
               <div className='reschedule-modal-actions'>
                  <div className='reschedule-action-block'>
                     <p className='exercise-block-field-label'>Запропонувати іншу частину дня</p>
                     <div className='reschedule-daypart-picker-row'>
                        {dayParts.map((dp) => (
                           <div key={dp.id} className={'exercise-block-type-chip' + (selectedDayPart === dp.id ? ' active-chip' : '')}
                              style={{ '--accent': '#7ec2e8' }} onClick={() => setSelectedDayPart(dp.id)}>
                              {dp.label}
                           </div>
                        ))}
                     </div>
                     <textarea className='exercise-block-generic-text' rows={2} placeholder='Коментар для учня (необов’язково)'
                        value={counterMessage} onChange={(e) => setCounterMessage(e.target.value)} />
                     <button type='button' className='exercise-block-save-btn' disabled={!selectedDayPart || saving} onClick={handleCounterOffer}>Надіслати пропозицію</button>
                  </div>

                  <div className='reschedule-action-block'>
                     <p className='exercise-block-field-label'>Або одразу змінити час заняття</p>
                     {!editingTime ? (
                        <button type='button' className='platform-tool-btn' onClick={() => openTimeEditor(lesson)}>Змінити дату/час</button>
                     ) : (
                        <div className='reschedule-time-editor'>
                           <DateInput clearable={false} className='reschedule-time-editor-date'
                              value={timeForm.date} onChange={(value) => setTimeForm((f) => ({ ...f, date: value }))} />
                           <TimeInput className='reschedule-time-editor-time'
                              value={timeForm.start} onChange={(value) => setTimeForm((f) => ({ ...f, start: value }))} />
                           <TimeInput className='reschedule-time-editor-time'
                              value={timeForm.end} onChange={(value) => setTimeForm((f) => ({ ...f, end: value }))} />
                           <button type='button' className='exercise-block-save-btn' disabled={saving} onClick={() => handleProposeTime(lesson.id)}>Зберегти новий час</button>
                           <button type='button' className='exercise-block-cancel-btn' onClick={() => setEditingTime(false)}>Скасувати</button>
                        </div>
                     )}
                  </div>

                  <div className='reschedule-action-block'>
                     <p className='exercise-block-field-label'>Немає вільного часу</p>
                     <textarea className='exercise-block-generic-text' rows={2} placeholder='Поясніть учню, чому (необов’язково)'
                        value={declineMessage} onChange={(e) => setDeclineMessage(e.target.value)} />
                     <button type='button' className='reschedule-decline-btn' disabled={saving} onClick={handleDecline}>Відхилити запит</button>
                  </div>
               </div>
            ) : ''}

            {detail.history && detail.history.length ? (
               <>
                  <p className='exercise-block-field-label' style={{ marginTop: 14 }}>Історія перемовин</p>
                  <div className='reschedule-history-list'>
                     {detail.history.map((h) => (
                        <div key={h.id} className={'reschedule-history-row actor-' + h.actorType}>
                           <span className='reschedule-history-actor'>{h.actorName || (h.actorType === 'TEACHER' ? 'Вчитель' : 'Учень')}</span>
                           <span className='reschedule-history-action'>{rescheduleActionLabel(h.action)}</span>
                           {h.message ? <span className='reschedule-history-message'>«{h.message}»</span> : ''}
                           <span className='reschedule-history-date'>{h.dAdd ? h.dAdd.replace('T', ' ').slice(0, 16) : ''}</span>
                        </div>
                     ))}
                  </div>
               </>
            ) : ''}
         </>
      );
   };

   const renderCancel = () => {
      const cr = detail.cancel;
      const event = cr.event || {};
      const lesson = event.lesson || {};
      const isActionable = cr.status === 'PENDING';

      return (
         <>
            <div className='reschedule-modal-lesson-info'>
               <span className='reschedule-modal-lesson-date'>{formatDateWeekday(lesson.date)}, {lesson.start}–{lesson.end}</span>
               <span className={'reschedule-modal-status-pill status-' + cr.status}>{cr.status === 'PENDING' ? 'Очікує на вашу відповідь' : cr.status === 'APPROVED' ? 'Схвалено' : 'Відхилено'}</span>
            </div>

            <p className='exercise-block-field-label'>Повідомлення від учня</p>
            <p className='reschedule-modal-message'>{cr.message || '—'}</p>

            {isActionable ? (
               <div className='reschedule-modal-actions'>
                  <div className='reschedule-action-block'>
                     <textarea className='exercise-block-generic-text' rows={2} placeholder='Коментар для учня (необов’язково)'
                        value={teacherMessage} onChange={(e) => setTeacherMessage(e.target.value)} />
                     <div className='reschedule-cancel-actions-row'>
                        <button type='button' className='exercise-block-save-btn' disabled={saving} onClick={handleApproveCancel}>Підтвердити скасування</button>
                        <button type='button' className='reschedule-decline-btn' disabled={saving} onClick={handleDecline}>Відхилити</button>
                     </div>
                  </div>
               </div>
            ) : (
               cr.teacherMessage ? <p className='reschedule-modal-message'>{cr.teacherMessage}</p> : ''
            )}
         </>
      );
   };

   return (
      <div className='modal-update' onClick={onClose} style={{ display: 'flex' }}>
         <div className='modal-window reschedule-modal-window' onClick={stopProp}>
            <div className='reschedule-modal-header'>
               <h3 className='modal-w-title'>{item.type === 'reschedule' ? 'Запит на перенесення заняття' : 'Запит на скасування заняття'}</h3>
               <div className='reschedule-modal-close' onClick={onClose}><IconCloseX style={{ width: 16, height: 16 }} /></div>
            </div>
            {status === 'loading' ? <p className='txt-c'>Завантаження...</p> : ''}
            {status === 'error' ? <p className='platform-modal-error'>Не вдалося завантажити запит</p> : ''}
            {status === 'done' && detail && item.type === 'reschedule' && detail.request ? renderReschedule() : ''}
            {status === 'done' && detail && item.type === 'cancel' && detail.cancel ? renderCancel() : ''}
            {error ? <p className='platform-modal-error'>{error}</p> : ''}
         </div>
      </div>
   );
}
